import { useUser, useClerk } from '@clerk/clerk-react';
import { useEffect, useState } from 'react';
import { syncClerkUserToSupabase, getUserProfile, type AuthUser } from '../services/auth';

export function useAuth() {
  const { user: clerkUser, isLoaded } = useUser();
  const { signOut: clerkSignOut } = useClerk();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sync Clerk user to Supabase and fetch profile on load
  useEffect(() => {
    async function syncUser() {
      if (!isLoaded) {
        setLoading(true);
        return;
      }

      if (!clerkUser) {
        setUser(null);
        setLoading(false);
        setError(null);
        return;
      }

      try {
        setError(null);

        // Sync Clerk user to Supabase
        await syncClerkUserToSupabase(clerkUser);

        // Get profile from Supabase
        const profile = await getUserProfile(clerkUser.id);

        setUser({
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || '',
          profile: profile || undefined,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error syncing user';
        console.error('Error syncing user:', err);
        setError(errorMessage);

        // Still set basic user info even if sync fails
        // This allows the user to continue using the app even if Supabase is down
        setUser({
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress || '',
        });

        // Only show alert for unexpected errors (not routine sync issues)
        if (!errorMessage.includes('Failed to sync user')) {
          alert(
            'Error syncing profile\n\n' +
            errorMessage + '\n\n' +
            'You can still use the app, but some features may not work.'
          );
        }
      } finally {
        setLoading(false);
      }
    }

    syncUser();
  }, [clerkUser, isLoaded]);

  const signOut = async () => {
    await clerkSignOut();
    setUser(null);
    setError(null);
  };

  return { user, loading, signOut, error };
}
